// components/common/TagInput.jsx
import React, { useState } from "react";

/**
 * Reusable TagInput component for creating and displaying tags/pills
 *
 * @param {Object} props
 * @param {string} props.label - Input field label
 * @param {Array} props.tags - Array of current tags
 * @param {Function} props.onAddTag - Function to call when adding a new tag
 * @param {Function} props.onRemoveTag - Function to call when removing a tag
 * @param {string} props.placeholder - Input placeholder text
 * @param {string} props.buttonColor - Custom button color class (default: "bg-emerald-800")
 * @param {string} props.buttonHoverColor - Custom button hover color class (default: "bg-emerald-900")
 */
function TagInput({
  label,
  tags = [],
  onAddTag,
  onRemoveTag,
  placeholder = "Type and press add...",
  buttonColor = "bg-emerald-800",
  buttonHoverColor = "bg-emerald-900",
}) {
  const [inputValue, setInputValue] = useState("");

  const handleAddTag = () => {
    if (inputValue.trim() !== "") {
      onAddTag(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    // Allow adding tags with Enter key
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  return (
    <div className="tag-input-container">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {/* Input and Add button */}
      <div className="flex mb-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1 p-3 border border-gray-300 rounded-l-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={handleAddTag}
          className={`${buttonColor} text-white px-4 py-2 rounded-r-md hover:${buttonHoverColor} shadow-sm`}
        >
          Add
        </button>
      </div>

      {/* Tags Display Container */}
      <div className="tags-container p-4 border border-gray-200 rounded-md bg-white">
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => (
            <div
              key={index}
              className="bg-gray-100 text-gray-700 rounded-full px-3 py-1 flex items-center text-sm border border-gray-300"
            >
              <span>{tag}</span>
              <button
                type="button"
                className="ml-2 text-gray-500 hover:text-gray-700 text-lg leading-none"
                onClick={() => onRemoveTag(index)}
                aria-label={`Remove ${tag}`}
              >
                ×
              </button>
            </div>
          ))}
          {tags.length === 0 && (
            <div className="text-gray-400 text-sm">No items added yet</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TagInput;
